import { UserServiceClient, LoggedInUserServiceClient, UserProjectsServiceClient, DemoProjectsServiceClient, AreaServiceClient, BillingServiceClient, LidarCoverageServiceClient, ElevationServiceClient, WorkspaceServiceClient, AddressSearchServiceClient } from '../../_proto/landscope-map-viewer_grpc_web_pb';
import { getAnalyticsDistinctId } from './posthogService';
class GrpcClientFactory {
    constructor() {
        // Resolve GRPC_ENDPOINT once during factory creation
        this.endpoint = GRPC_ENDPOINT;
    }
    /**
     * Get authentication tokens from localStorage
     */
    getAuthTokens() {
        const sessionId = localStorage.getItem('landscope_session_id');
        const sessionToken = localStorage.getItem('landscope_session_token');
        if (!sessionId || !sessionToken) {
            return null;
        }
        return { sessionId, sessionToken };
    }
    /**
     * Create base client options with optional compression and auth
     */
    createClientOptions(options = {}) {
        const clientOptions = {};
        // Send cookies on every request. In production the app and api live on
        // different subdomains, and signup attribution depends on the attribution
        // cookies (written by the app after consent) reaching the backend; without
        // this the browser strips them and every signup reports as Organic. Dev
        // is same-origin and unaffected.
        // Pairs with Access-Control-Allow-Credentials on the api Caddy block.
        clientOptions.withCredentials = true;
        // PostHog distinct_id interceptor: the header is sent only while PostHog
        // runs under Statistics consent, so server-side events stay consented too.
        const posthogInterceptor = {
            intercept: (request, invoker) => {
                const metadata = request.getMetadata();
                const distinctId = getAnalyticsDistinctId();
                if (distinctId) {
                    metadata['x-posthog-distinct-id'] = distinctId;
                }
                return invoker(request);
            }
        };
        clientOptions.unaryInterceptors = [posthogInterceptor];
        clientOptions.streamInterceptors = [posthogInterceptor];
        // Add compression support with proper headers
        if (options.enableCompression !== false) {
            // Add compression interceptor that sets the grpc-accept-encoding header
            const compressionInterceptor = {
                intercept: (request, invoker) => {
                    const metadata = request.getMetadata();
                    // Request brotli and gzip compression from server
                    metadata['grpc-accept-encoding'] = 'br, gzip';
                    // Optionally request specific encoding for outgoing messages
                    // metadata['grpc-encoding'] = 'br';
                    return invoker(request);
                }
            };
            clientOptions.unaryInterceptors.push(compressionInterceptor);
            clientOptions.streamInterceptors.push(compressionInterceptor);
        }
        // Add authentication interceptor if requested
        if (options.withAuth) {
            const tokens = this.getAuthTokens();
            if (!tokens) {
                throw new Error('No authentication tokens available for authenticated client');
            }
            const authInterceptor = {
                intercept: (request, invoker) => {
                    const metadata = request.getMetadata();
                    metadata['authorization'] = `Bearer ${tokens.sessionId}|${tokens.sessionToken}`;
                    return invoker(request);
                }
            };
            // Initialize interceptor arrays if they don't exist
            if (!clientOptions.unaryInterceptors) {
                clientOptions.unaryInterceptors = [];
            }
            if (!clientOptions.streamInterceptors) {
                clientOptions.streamInterceptors = [];
            }
            // Add auth interceptor to both arrays
            clientOptions.unaryInterceptors.push(authInterceptor);
            clientOptions.streamInterceptors.push(authInterceptor);
        }
        // Add conditional authentication interceptor if requested
        if (options.withConditionalAuth) {
            const conditionalAuthInterceptor = {
                intercept: (request, invoker) => {
                    // Check for tokens at request time (not client creation time)
                    const tokens = this.getAuthTokens();
                    if (tokens) {
                        const metadata = request.getMetadata();
                        metadata['authorization'] = `Bearer ${tokens.sessionId}|${tokens.sessionToken}`;
                    }
                    // Always proceed with the request, whether auth tokens are present or not
                    return invoker(request);
                }
            };
            // Initialize interceptor arrays if they don't exist
            if (!clientOptions.unaryInterceptors) {
                clientOptions.unaryInterceptors = [];
            }
            if (!clientOptions.streamInterceptors) {
                clientOptions.streamInterceptors = [];
            }
            // Add conditional auth interceptor to both arrays
            clientOptions.unaryInterceptors.push(conditionalAuthInterceptor);
            clientOptions.streamInterceptors.push(conditionalAuthInterceptor);
        }
        return clientOptions;
    }
    // KmzServiceClient removed - service no longer exists in proto
    /**
     * Create UserServiceClient
     */
    createUserClient(options = {}) {
        const clientOptions = this.createClientOptions(options);
        return new UserServiceClient(this.endpoint, null, clientOptions);
    }
    /**
     * Create LoggedInUserServiceClient
     */
    createLoggedInUserClient(options = {}) {
        const clientOptions = this.createClientOptions({ ...options, withAuth: true });
        return new LoggedInUserServiceClient(this.endpoint, null, clientOptions);
    }
    /**
     * Create authenticated WorkspaceServiceClient
     */
    createWorkspaceClient(options = {}) {
        const clientOptions = this.createClientOptions({ ...options, withAuth: true });
        return new WorkspaceServiceClient(this.endpoint, null, clientOptions);
    }
    /**
     * Create UserProjectsServiceClient
     */
    createUserProjectsClient(options = {}) {
        const clientOptions = this.createClientOptions(options);
        return new UserProjectsServiceClient(this.endpoint, null, clientOptions);
    }
    /**
     * Create authenticated UserProjectsServiceClient
     */
    createAuthenticatedUserProjectsClient(options = {}) {
        const clientOptions = this.createClientOptions({ ...options, withAuth: true });
        return new UserProjectsServiceClient(this.endpoint, null, clientOptions);
    }
    /**
     * Create DemoProjectsServiceClient
     */
    createDemoProjectsClient(options = {}) {
        const clientOptions = this.createClientOptions(options);
        return new DemoProjectsServiceClient(this.endpoint, null, clientOptions);
    }
    /**
     * Create LidarCoverageServiceClient
     */
    createLidarCoverageClient(options = {}) {
        const clientOptions = this.createClientOptions(options);
        return new LidarCoverageServiceClient(this.endpoint, null, clientOptions);
    }
    /**
     * Create ElevationServiceClient
     */
    createElevationClient(options = {}) {
        const clientOptions = this.createClientOptions(options);
        return new ElevationServiceClient(this.endpoint, null, clientOptions);
    }
    /**
     * Create AreaServiceClient
     */
    createAreaClient(options = {}) {
        const clientOptions = this.createClientOptions(options);
        return new AreaServiceClient(this.endpoint, null, clientOptions);
    }
    /**
     * Create AddressSearchServiceClient
     */
    createAddressSearchClient(options = {}) {
        const clientOptions = this.createClientOptions(options);
        return new AddressSearchServiceClient(this.endpoint, null, clientOptions);
    }
    /**
     * Create BillingServiceClient
     */
    createBillingClient(options = {}) {
        const clientOptions = this.createClientOptions(options);
        return new BillingServiceClient(this.endpoint, null, clientOptions);
    }
    /**
     * Create authenticated BillingServiceClient
     */
    createAuthenticatedBillingClient(options = {}) {
        const clientOptions = this.createClientOptions({ ...options, withAuth: true });
        return new BillingServiceClient(this.endpoint, null, clientOptions);
    }
    /**
     * Plain HTTP request to the API host, for the endpoints that are not gRPC
     * (lead capture). Credentialed for the same reason the clients above are:
     * the attribution cookies only reach the backend on requests that ask.
     */
    fetchApi(path, init = {}) {
        return fetch(`${this.endpoint}${path}`, { ...init, credentials: 'include' });
    }
}
// Export singleton instance
export const grpcClientFactory = new GrpcClientFactory();
